import DataLoader from 'dataloader';
import { DeathCertificate } from '../types';

import fetchDeathCertificates from '../queries/fetch-death-certificates';
import searchDeathCertificates from '../queries/search-death-certificates';
import lookupDeathCertificateOrder from '../queries/lookup-death-certificate-order';
import { DeathCertificateSearchResults, DeathCertificateOrder } from '../types';
import { FetchGraphql } from '@cityofboston/next-client-common';

export type DeathCertificateCache = { [id: string]: DeathCertificate };

export default class DeathCertificatesDao {
  fetchGraphql: FetchGraphql;
  loader: DataLoader<string, DeathCertificate | null>;

  cacheFullQuery: string | null = null;
  cacheByPage: { [page: number]: DeathCertificateSearchResults } = {};

  constructor(fetchGraphql: FetchGraphql) {
    this.fetchGraphql = fetchGraphql;

    // create new array shenanigans to get Flow to accept that we're not returning Errors
    this.loader = new DataLoader(ids =>
      fetchDeathCertificates(fetchGraphql, ids).then(a => [...a])
    );
  }

  get(id: string): Promise<DeathCertificate | null> {
    return this.loader.load(id);
  }

  async search(
    fullQuery: string,
    page: number
  ): Promise<any> /*Promise<DeathCertificateSearchResults>*/ {
    if (fullQuery === this.cacheFullQuery) {
      const results = this.cacheByPage[page];
      if (results) {
        return results;
      }
    } else {
      this.cacheFullQuery = fullQuery;
      this.cacheByPage = {};
    }

    const { query, startYear, endYear } = this.parseQuery(fullQuery);

    try {
      const results = await searchDeathCertificates(
        this.fetchGraphql,
        query,
        page,
        startYear,
        endYear
      );

      this.cacheByPage[page] = results;

      // eslint-disable-next-line no-console
      // console.log(
      //   'DeathCertificateDao > this.cacheByPage: ',
      //   this.cacheByPage[page]
      // );

      results.results.forEach(cert => {
        this.loader.prime(cert.id, cert);
      });

      return results;
    } catch (error) {
      console.log('Error talking to GraphQL endpoint');
    }
  }

  async lookupOrder(
    id: string,
    contactEmail: string
  ): Promise<DeathCertificateOrder | null> {
    return lookupDeathCertificateOrder(this.fetchGraphql, id, contactEmail);
  }

  parseQuery(
    fullQuery: string
  ): { query: string; startYear: string | null; endYear: string | null } {
    // match a 4-digit year, and optionally a second 4-digit year with a hypen or en-dash
    const yearRegexp = /(\d{4})\s*[-–]?\s*(\d{4})?/;

    let query;
    let startYear;
    let endYear;

    const match = fullQuery.match(yearRegexp);
    if (match) {
      query = fullQuery.replace(yearRegexp, '').trim();
      startYear = match[1];
      endYear = match[2] || match[1];
    } else {
      query = fullQuery;
      startYear = null;
      endYear = null;
    }

    return {
      query,
      startYear,
      endYear,
    };
  }
}
